import { useState, useEffect, useCallback } from 'react';
import { apiService, ApiError } from '../services/apiService';

// Generic hook for API calls
export function useApi<T>(
  apiCall: () => Promise<{ data: T; success: boolean }>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await apiCall();
      setData(response.data);
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('API Error:', err);
      
      // For development, set mock data to prevent UI crashes
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock data for development');
        setData(null); // Let components handle null data gracefully
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependencies are passed in by caller
  }, dependencies);

  const refresh = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dependencies are passed in by caller
  }, dependencies);

  return {
    data,
    loading,
    error,
    refreshing,
    refresh,
    refetch: fetchData,
  };
}

// Specific hooks for common API calls
const SYSTEM_STATUS_POLL_MS = 30_000;

export function useSystemStatus() {
  const apiCall = useCallback(() => apiService.getSystemStatus(), []);
  const { refetch, ...rest } = useApi(apiCall, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      refetch(true);
    }, SYSTEM_STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [refetch]);

  return { refetch, ...rest };
}

export function usePerformanceMetrics() {
  const apiCall = useCallback(() => apiService.getPerformanceMetrics(), []);
  return useApi(apiCall, []);
}

export function useAlerts() {
  const apiCall = useCallback(() => apiService.getAlerts(), []);
  return useApi(apiCall, []);
}

export function useAlertHistory(page = 1, limit = 20) {
  const apiCall = useCallback(() => apiService.getAlertHistory(page, limit), [page, limit]);
  return useApi(apiCall, [page, limit]);
}

export function useWebhookMappings() {
  const apiCall = useCallback(() => apiService.getWebhookMappings(), []);
  return useApi(apiCall, []);
}

export function useSafetyStatus() {
  const apiCall = useCallback(() => apiService.getSafetyStatus(), []);
  return useApi(apiCall, []);
}

export function useConfig() {
  const apiCall = useCallback(() => apiService.getConfig(), []);
  return useApi(apiCall, []);
}

// Hook for API operations that require user interaction
export function useApiOperation<T>(
  operation: (...params: any[]) => Promise<{ data: T; success: boolean }>
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);

  const execute = useCallback(async (...params: any[]) => {
    try {
      setLoading(true);
      setError(null);
      setData(null);

      const response = await operation(...params);
      setData(response.data);
      return response;
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('API Operation Error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [operation]);

  const reset = useCallback(() => {
    setError(null);
    setData(null);
  }, []);

  return {
    execute,
    loading,
    error,
    data,
    reset,
  };
}

// Hook for webhook operations
export function useWebhookOperations() {
  const createMapping = useApiOperation(apiService.createWebhookMapping);
  const updateMapping = useApiOperation(apiService.updateWebhookMapping);
  const deleteMapping = useApiOperation(apiService.deleteWebhookMapping);
  const testWebhook = useApiOperation(apiService.testWebhook);

  return {
    createMapping,
    updateMapping,
    deleteMapping,
    testWebhook,
  };
}

// Hook for safety operations
export function useSafetyOperations() {
  const emergencyStop = useApiOperation(apiService.triggerEmergencyStop);

  return {
    emergencyStop,
  };
}
